import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for regular auth session
    if (authService.isAuthenticated()) {
      setUser(authService.getUser());
    } else {
      // Check for demo session
      const demoSession = localStorage.getItem('demo_session');
      if (demoSession) {
        try {
          const session = JSON.parse(demoSession);
          const expiresAt = new Date(session.expires_at);
          if (expiresAt > new Date()) {
            setUser(session.user);
          } else {
            localStorage.removeItem('demo_session');
          }
        } catch (error) {
          localStorage.removeItem('demo_session');
        }
      }
    }
    setLoading(false);
  }, []);

  const login = (sessionData) => {
    if (sessionData.session_token && sessionData.user) {
      // Regular login
      authService.setSession(sessionData.session_token, sessionData.user);
      setUser(sessionData.user);
    } else {
      // Legacy support
      authService.setSession(sessionData, sessionData);
      setUser(sessionData);
    }
  };

  const logout = () => {
    // Clear regular auth session
    authService.logout();
    
    // Clear demo session
    localStorage.removeItem('demo_session');
    
    // Clear any other auth-related data
    localStorage.removeItem('user_data');
    localStorage.removeItem('session_token');
    
    // Force update the user state
    setUser(null);
    
    // Clear axios default headers
    delete axios.defaults.headers.common['Authorization'];
    
    console.log('Logout completed - all sessions cleared');
  };

  const handleGoogleLogin = () => {
    authService.initiateGoogleLogin();
  };

  const handleGoogleCallback = async (sessionId) => {
    const result = await authService.handleGoogleCallback(sessionId);
    if (result.success) {
      setUser(result.user);
    }
    return result;
  };

  const sendPhoneVerification = async (phoneNumber) => {
    return await authService.sendPhoneVerification(phoneNumber);
  };

  const verifyPhoneCode = async (phoneNumber, code) => {
    const result = await authService.verifyPhoneCode(phoneNumber, code);
    if (result.success) {
      setUser(result.user);
    }
    return result;
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('user_data', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    loading,
    login,
    logout,
    handleGoogleLogin,
    handleGoogleCallback,
    sendPhoneVerification,
    verifyPhoneCode,
    updateUser,
    isAuthenticated: authService.isAuthenticated()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};